// Kho công việc (mock, lưu localStorage — sẽ thay bằng API khi có backend)
import { create } from 'zustand';
import dayjs from 'dayjs';
import { persist } from 'zustand/middleware';
import { projectOf, seedTasks, type Repeat, type TaskActivity, type WorkTask } from './data';
import { applyAction, isClosed, nextOccurrence, submitBlocker, ymd, type Action, type TransitionInput } from './flow';

export const WORK_STORAGE_KEY = 'khothi-work-v1';

export interface NewTaskInput {
  name: string;
  description?: string;
  projectId: string;
  parentId?: string;
  assigneeId?: string;
  followerIds: string[];
  startAt?: string;
  dueAt?: string;
  important: boolean;
  urgent: boolean;
  needReview: boolean;
  checklist?: string[];
  needProof?: boolean;
  repeat?: Repeat;
  templateId?: string;
}

export type TaskPatch = Partial<Pick<WorkTask, 'name' | 'description' | 'important' | 'urgent' | 'needReview' | 'followerIds' | 'startAt' | 'needProof' | 'repeat'>>;

interface WorkState {
  tasks: WorkTask[];
  seq: number;
  createTask: (input: NewTaskInput, by: string) => WorkTask;
  /** Trả về lỗi (string) hoặc null; việc định kỳ vừa đóng thì tự tạo lần kế tiếp */
  act: (id: string, action: Action, input: TransitionInput) => string | null;
  updateTask: (id: string, patch: TaskPatch, by: string) => void;
  comment: (id: string, text: string, by: string) => void;
  /** Tích / bỏ tích một mục checklist (ghi người tích và thời điểm) */
  check: (id: string, itemId: string, done: boolean, by: string) => void;
  addCheckItem: (id: string, text: string, by: string) => void;
  /** Dời lịch các việc đang mở của dự án; trả về số việc đã dời */
  shiftTasks: (projectId: string, days: number, by: string) => number;
}

const newAct = (by: string, type: TaskActivity['type'], text?: string): TaskActivity => ({
  id: `act-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`, at: new Date().toISOString(), by, type, text,
});

const uid = (p: string) => `${p}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

const useWorkStore = create<WorkState>()(persist((set, get) => {
  const seeded = seedTasks();
  const replace = (t: WorkTask) => set(s => ({ tasks: s.tasks.map(x => (x.id === t.id ? t : x)) }));

  /** Lần kế tiếp của việc định kỳ — chỉ tạo khi chuỗi chưa có lần nào đang mở */
  const spawnNext = (t: WorkTask) => {
    const next = nextOccurrence(t);
    const series = t.seriesId ?? t.id;
    if (!next || get().tasks.some(x => (x.seriesId ?? x.id) === series && x.id !== t.id && !isClosed(x))) return;
    const seq = get().seq + 1;
    const task: WorkTask = {
      ...t,
      ...next,
      id: uid('wt'),
      code: `CV-${seq}`,
      seriesId: series,
      createdAt: ymd(),
      status: 'todo',
      submittedAt: undefined,
      doneAt: undefined,
      checklist: t.checklist?.map(c => ({ id: uid('ck'), text: c.text })),
      activity: [newAct(t.creatorId, 'create', `Tự tạo lần tiếp theo sau ${t.code}`), ...(t.assigneeId ? [newAct(t.creatorId, 'assign', t.assigneeId)] : [])],
    };
    set(s => ({ tasks: [task, ...s.tasks], seq }));
  };

  return {
    tasks: seeded,
    seq: 1000 + seeded.length,

    createTask: ({ checklist, ...input }, by) => {
      const seq = get().seq + 1;
      const today = ymd();
      const activity = [newAct(by, 'create')];
      if (input.assigneeId) activity.push(newAct(by, 'assign', input.assigneeId));
      const id = `wt-${Date.now().toString(36)}`;
      const task: WorkTask = {
        ...input,
        id,
        code: `CV-${seq}`,
        departmentId: projectOf(input.projectId)?.departmentId ?? '',
        creatorId: by,
        createdAt: today,
        startAt: input.startAt ?? today,
        status: 'todo',
        checklist: checklist?.map(s => s.trim()).filter(Boolean).map(text => ({ id: uid('ck'), text })),
        seriesId: input.repeat ? id : undefined,
        activity,
      };
      set(s => ({ tasks: [task, ...s.tasks], seq }));
      return task;
    },

    act: (id, action, input) => {
      const { tasks } = get();
      const t = tasks.find(x => x.id === id);
      if (!t) return 'Không tìm thấy công việc';
      if (action === 'submit') {
        const blocker = submitBlocker(t, tasks);
        if (blocker) return blocker;
      }
      const next = applyAction(t, action, input);
      if (typeof next === 'string') return next;
      replace(next);
      if (!isClosed(t) && isClosed(next)) spawnNext(next);
      return null;
    },

    updateTask: (id, patch, by) => {
      const t = get().tasks.find(x => x.id === id);
      if (!t) return;
      const seriesId = patch.repeat && !t.seriesId ? t.id : t.seriesId;
      replace({ ...t, ...patch, seriesId, activity: [...t.activity, newAct(by, 'edit')] });
    },

    comment: (id, text, by) => {
      const t = get().tasks.find(x => x.id === id);
      if (!t || !text.trim()) return;
      replace({ ...t, activity: [...t.activity, newAct(by, 'comment', text.trim())] });
    },

    check: (id, itemId, done, by) => {
      const t = get().tasks.find(x => x.id === id);
      if (!t?.checklist) return;
      replace({ ...t, checklist: t.checklist.map(c => (c.id === itemId ? (done ? { ...c, done: true, by, at: new Date().toISOString() } : { id: c.id, text: c.text }) : c)) });
    },

    addCheckItem: (id, text, by) => {
      const t = get().tasks.find(x => x.id === id);
      if (!t || !text.trim()) return;
      replace({ ...t, checklist: [...(t.checklist ?? []), { id: uid('ck'), text: text.trim() }], activity: [...t.activity, newAct(by, 'edit', `Thêm mục checklist: ${text.trim()}`)] });
    },

    shiftTasks: (projectId, days, by) => {
      const move = (d?: string) => (d ? dayjs(d).add(days, 'day').format('YYYY-MM-DD') : d);
      let n = 0;
      set(s => ({
        tasks: s.tasks.map(t => {
          if (t.projectId !== projectId || isClosed(t)) return t;
          n++;
          return { ...t, startAt: move(t.startAt), dueAt: move(t.dueAt), activity: [...t.activity, newAct(by, 'due', `Dời theo lịch dự án (${days > 0 ? '+' : ''}${days} ngày)`)] };
        }),
      }));
      return n;
    },
  };
  // v2: dữ liệu mẫu theo phòng khám (phòng chuyên môn, việc định kỳ theo quy trình) — tạo lại từ seed
}, {
  name: WORK_STORAGE_KEY,
  version: 2,
  migrate: () => {
    const tasks = seedTasks();
    return { tasks, seq: 1000 + tasks.length } as WorkState;
  },
}));

export default useWorkStore;
